var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable no-nested-ternary */
import React from 'react';
import { cx, css as emotionCss } from 'emotion';
import { getChildByType, removeChildrenByType } from 'react-nanny';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { useLocalization, useTheme } from '@citrus/user';
import { get, isBoolean, isNotNilOrEmpty, isNullOrEmpty } from '@pcty/utils';
import { disabled as disabledCssLegacy, iconOnly, iconOnlyBordered as iconOnlyBorderedLegacy, large, medium, small, } from '../styles/legacy';
import { iconOnly as iconOnlyCitron, iconOnlyBordered as iconOnlyBorderedCitron, large as largeCitron, medium as mediumCitron, small as smallCitron, disabled as disabledCssCitron, } from '../styles/citron';
import { Badge as BadgeStyled, BadgeSecondary, BadgeDefault, ButtonContainer, ButtonIconSmall, ButtonIconMedium, ButtonIconLarge, ButtonLoader, ButtonText, DropdownIcon, DropdownIconSecondary, DropdownIconDefault, SplitButtonLeft, SplitButtonRight, SplitButtonLeftSecondary, SplitButtonRightSecondary, SplitButtonLeftDefault, SplitButtonRightDefault, SplitButtonWrapper, } from './Button.styled';
import localizationMap from '../localization';
var VERSION = '5.11.2';
var SplitButton = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var automationId = _a.automationId, badge = _a.badge, _d = _a.buttonType, buttonType = _d === void 0 ? 'button' : _d, children = _a.children, className = _a.className, css = _a.css, _e = _a.disabled, disabled = _e === void 0 ? { left: false, right: false } : _e, flyoutId = _a.flyoutId, id = _a.id, onClick = _a.onClick, onClickDropdown = _a.onClickDropdown, _f = _a.processing, processing = _f === void 0 ? false : _f, _g = _a.size, size = _g === void 0 ? 'medium' : _g, _h = _a.type, type = _h === void 0 ? 'primary' : _h, _j = _a.visible, visible = _j === void 0 ? true : _j, _k = _a.__TYPE, __TYPE = _k === void 0 ? 'SplitButton' : _k, other = __rest(_a, ["automationId", "badge", "buttonType", "children", "className", "css", "disabled", "flyoutId", "id", "onClick", "onClickDropdown", "processing", "size", "type", "visible", "__TYPE"]);
    var theme = useTheme();
    var lang = useLocalization(localizationMap);
    if (!visible)
        return null;
    var sizes = theme.name === 'legacy'
        ? {
            small: small,
            medium: medium,
            large: large,
        }
        : {
            small: smallCitron,
            medium: mediumCitron,
            large: largeCitron,
        };
    var icon = getChildByType(children, ['Icon', 'PctyIcon']);
    children = removeChildrenByType(children, ['Icon', 'PctyIcon']);
    if (isBoolean(disabled)) {
        disabled = { left: disabled, right: disabled };
    }
    var disabledCss = theme.legacy ? disabledCssLegacy : disabledCssCitron;
    var iconOnlyBordered = theme.legacy ? iconOnlyBorderedLegacy : iconOnlyBorderedCitron;
    var emotion = {
        disabled: emotionCss(disabledCss),
        css: emotionCss(css),
    };
    var getClasses = function (side) {
        var _a;
        return (_a = {},
            _a[emotionCss(sizes[size])] = true,
            _a[emotionCss(iconOnlyBordered)] = side === 'right' || (icon && isNullOrEmpty(children)),
            _a[emotion.disabled] = disabled[side],
            _a[emotion.css] = !!css,
            _a);
    };
    var ButtonLeft = type === 'secondary' ? SplitButtonLeftSecondary : type === 'default' ? SplitButtonLeftDefault : SplitButtonLeft;
    var ButtonRight = type === 'secondary'
        ? SplitButtonRightSecondary
        : type === 'default'
            ? SplitButtonRightDefault
            : SplitButtonRight;
    var DropDownButtonIcon = type === 'secondary' ? DropdownIconSecondary : type === 'default' ? DropdownIconDefault : DropdownIcon;
    var Badge = type === 'secondary' ? BadgeSecondary : type === 'default' ? BadgeDefault : BadgeStyled;
    var classNameLeft = getClasses('left');
    var classNameRight = getClasses('right');
    var styleNamesLeft = (_b = {},
        _b[size] = true,
        _b.iconOnlyBordered = classNameLeft[emotionCss(iconOnlyBordered)],
        _b.disabled = classNameLeft[emotion.disabled],
        _b);
    var styleNamesRight = (_c = {},
        _c[size] = true,
        _c.iconOnlyBordered = classNameRight[emotionCss(iconOnlyBordered)],
        _c.disabled = classNameRight[emotion.disabled],
        _c);
    var buttonSizeToIconComponent = new Map([
        ['small', ButtonIconSmall],
        ['medium', ButtonIconMedium],
        ['large', ButtonIconLarge],
    ]);
    var ButtonIcon = buttonSizeToIconComponent.get(size);
    return (React.createElement(SplitButtonWrapper, __assign({}, getDebug(sessionStorage.getItem('citrus-debug') === 'true', __TYPE, VERSION), { ref: ref, role: "group" }),
        React.createElement(ButtonLeft, __assign({ className: cx(classNameLeft, className || ''), disabled: disabled.left, key: id + '-button-left', onClick: get(disabled, 'left', false) || processing ? undefined : onClick, styleNames: getStyleNames(styleNamesLeft) }, getAutomation(automationId), (buttonType ? { type: buttonType } : ''), other),
            React.createElement(ButtonContainer, null,
                renderLoader(processing),
                renderIcon(children, icon, size, theme, badge),
                renderBadge(Badge, automationId, badge),
                renderText(children))),
        React.createElement(ButtonRight, __assign({ className: cx(classNameRight, className || ''), disabled: disabled.right, key: id + '-button-right', onClick: get(disabled, 'right', false) || processing ? undefined : onClickDropdown, styleNames: getStyleNames(styleNamesRight) }, getAutomation(automationId, 'dropdown'), { "data-pcty-flyout-id": flyoutId }),
            React.createElement(ButtonContainer, null,
                React.createElement(ButtonIcon, { className: emotionCss(iconOnly[size]) },
                    React.createElement(DropDownButtonIcon, { "aria-label": lang.openMenu }))))));
});
var renderBadge = function (Badge, automationId, badge) {
    return isNotNilOrEmpty(badge) ? React.createElement(Badge, __assign({}, getAutomation(automationId, 'badge')), badge) : undefined;
};
var renderLoader = function (processing) { return (processing ? React.createElement(ButtonLoader, null) : undefined); };
var renderIcon = function (children, icon, size, theme, badge) {
    if (badge || isNullOrEmpty(icon))
        return;
    var buttonSizeToIconSize = new Map([
        ['small', 'xsmall'],
        ['medium', 'small'],
        ['large', 'medium'],
    ]);
    var buttonSizeToIconComponent = new Map([
        ['small', ButtonIconSmall],
        ['medium', ButtonIconMedium],
        ['large', ButtonIconLarge],
    ]);
    var _icon = React.cloneElement(icon, { bold: true, size: buttonSizeToIconSize.get(size) });
    var _iconOnly = theme.name === 'legacy' ? iconOnly : iconOnlyCitron;
    var ButtonIcon = buttonSizeToIconComponent.get(size);
    if (isNullOrEmpty(children)) {
        return React.createElement(ButtonIcon, { className: emotionCss(_iconOnly[size]) }, _icon);
    }
    return React.createElement(ButtonIcon, null, _icon);
};
var renderText = function (children) {
    return children ? React.createElement(ButtonText, null, children) : undefined;
};
SplitButton.defaultProps = {
    __TYPE: 'SplitButton',
};
export default SplitButton;
